import { ChevronDown, ChevronRight } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";

/**
 * Hand-rolled collapsible JSON tree, ~70 lines including the body
 * dispatch. The dashboard spec calls for this rather than pulling in
 * `react-json-view` because the trees are small (a handful of fields,
 * 2-3 levels deep in practice) and the bundle saving is real.
 *
 * For non-JSON bodies (Dealerweb's SOAP XML) the spec says "monospaced
 * text with line numbers." `BodyViewer` below tries `JSON.parse` first
 * and falls back to a numbered <pre> block on failure.
 */

export function PayloadViewer({ value }: { value: unknown }): JSX.Element {
  return (
    <div className="font-mono text-xs leading-relaxed">
      <JsonNode value={value} depth={0} />
    </div>
  );
}

function JsonNode({
  value,
  depth,
}: {
  value: unknown;
  depth: number;
}): JSX.Element {
  if (value === null)
    return <span className="text-muted-foreground">null</span>;
  if (typeof value === "string")
    return (
      <span className="text-emerald-700 break-all">
        {JSON.stringify(value)}
      </span>
    );
  if (typeof value === "number")
    return <span className="text-blue-700">{String(value)}</span>;
  if (typeof value === "boolean")
    return <span className="text-purple-700">{String(value)}</span>;
  if (Array.isArray(value))
    return <ArrayNode value={value} depth={depth} />;
  if (typeof value === "object")
    return (
      <ObjectNode value={value as Record<string, unknown>} depth={depth} />
    );
  return <span>{String(value)}</span>;
}

function ObjectNode({
  value,
  depth,
}: {
  value: Record<string, unknown>;
  depth: number;
}): JSX.Element {
  // Top-level objects expanded by default; nested objects collapsed so
  // a deep config_snapshot doesn't dump the whole tree on first render.
  const [expanded, setExpanded] = useState(depth < 1);
  const keys = Object.keys(value);
  if (keys.length === 0) {
    return <span className="text-muted-foreground">{"{}"}</span>;
  }
  return (
    <div>
      <button
        type="button"
        className="inline-flex items-center text-muted-foreground hover:text-foreground"
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <span className="ml-1">
          {expanded ? "{" : `{ ${keys.length} key${keys.length === 1 ? "" : "s"} }`}
        </span>
      </button>
      {expanded && (
        <div className="ml-4 border-l border-border pl-3">
          {keys.map((key) => (
            <div key={key} className="flex">
              <span className="mr-2 text-foreground">{key}:</span>
              <JsonNode value={value[key]} depth={depth + 1} />
            </div>
          ))}
          <div className="text-muted-foreground">{"}"}</div>
        </div>
      )}
    </div>
  );
}

function ArrayNode({
  value,
  depth,
}: {
  value: unknown[];
  depth: number;
}): JSX.Element {
  const [expanded, setExpanded] = useState(depth < 1);
  if (value.length === 0) {
    return <span className="text-muted-foreground">[]</span>;
  }
  return (
    <div>
      <button
        type="button"
        className="inline-flex items-center text-muted-foreground hover:text-foreground"
        onClick={() => setExpanded((e) => !e)}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <span className="ml-1">
          {expanded
            ? "["
            : `[ ${value.length} item${value.length === 1 ? "" : "s"} ]`}
        </span>
      </button>
      {expanded && (
        <div className="ml-4 border-l border-border pl-3">
          {value.map((item, idx) => (
            <div key={idx} className="flex">
              <span className="mr-2 text-muted-foreground">{idx}:</span>
              <JsonNode value={item} depth={depth + 1} />
            </div>
          ))}
          <div className="text-muted-foreground">]</div>
        </div>
      )}
    </div>
  );
}

/**
 * Render a wire-format string body. JSON-shaped strings get the tree
 * viewer; XML or other text drops to a numbered monospace block, per
 * the dashboard spec's "monospaced text with line numbers" rule.
 */
export function BodyViewer({ body }: { body: string | null }): JSX.Element {
  if (body === null || body === "") {
    return (
      <p className="font-mono text-xs text-muted-foreground">(empty)</p>
    );
  }
  try {
    const parsed = JSON.parse(body);
    return <PayloadViewer value={parsed} />;
  } catch {
    return <NumberedText text={body} />;
  }
}

function NumberedText({ text }: { text: string }): JSX.Element {
  const lines = text.split("\n");
  return (
    <div className="overflow-x-auto rounded-md bg-muted/50 p-2 font-mono text-xs">
      {lines.map((line, idx) => (
        <div key={idx} className="flex">
          <span
            className={cn(
              "mr-3 inline-block w-8 shrink-0 select-none text-right text-muted-foreground",
            )}
          >
            {idx + 1}
          </span>
          <span className="whitespace-pre-wrap break-all">{line}</span>
        </div>
      ))}
    </div>
  );
}
